<template>
  <div class="app-container">
    <!-- 头部导航 -->
    <header>
      <div class="container">
        <div class="header-wrap">
          <div class="logo">
            <span class="sigma-tag">Sigma</span>
            <span>佛山市伟邺钢铁有限公司</span>
          </div>
          <nav>
            <ul :class="{ active: menuOpen }" id="nav-list">
              <li v-for="item in navItems" :key="item.id">
                <a
                    :href="'#' + item.id"
                    :class="{ active: activeLink === item.id }"
                    @click.prevent="scrollToSection(item.id)"
                >{{ item.name }}</a>
              </li>
            </ul>
          </nav>
          <div class="mobile-menu" @click="toggleMenu">{{ menuOpen ? '✕' : '☰' }}</div>
        </div>
      </div>
    </header>

    <!-- 首屏 Banner -->
    <section class="banner" id="home">
      <div class="container">
        <div class="banner-content">
          <h1>佛山市伟邺钢铁有限公司</h1>
          <p>光伏发电 · 防雷接地 · 中央空调管道 · 消防管道 工程钢材专业配套服务商</p>
          <div class="stats-row">
            <div class="stat-item"><strong>5000㎡</strong> 仓储面积</div>
            <div class="stat-item"><strong>10000吨</strong> 常备库存</div>
            <div class="stat-item"><strong>15年</strong> 行业经验</div>
            <div class="stat-item"><strong>5000万+</strong> 年销售额</div>
          </div>
          <div class="banner-btn">
            <a href="#service" @click.prevent="scrollToSection('service')" class="btn-primary">加工服务</a>
            <a href="#contact" @click.prevent="scrollToSection('contact')" class="btn-outline">获取报价</a>
          </div>
        </div>
      </div>
    </section>

    <!-- 企业介绍模块（动态数据优先，静态文案为后备） -->
    <section class="section" id="about">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 企业介绍
        </div>
        <div class="section-title">
          <h2>{{ aboutData?.title || '公司概况' }}</h2>
          <p>{{ aboutData?.subtitle || '专注钢材批发、加工、运输15年，致力成为华南地区最具竞争力的钢铁供应链服务商' }}</p>
        </div>
        <div class="about-wrap" v-if="aboutData">
          <div class="about-img">
            <img :src="getImageUrl(aboutData.image)" :alt="aboutData.contentTitle || '伟邺钢铁仓库实景'" loading="lazy">
          </div>
          <div class="about-content">
            <h3>{{ aboutData.contentTitle || '佛山市伟邺钢铁有限公司' }}</h3>
            <p v-for="(para, idx) in (aboutData.paragraphs && aboutData.paragraphs.length ? aboutData.paragraphs : defaultAboutParagraphs)"
               :key="idx">{{ para }}</p>
            <div class="about-data">
              <div v-for="(stat, idx) in (aboutData.stats && aboutData.stats.length ? aboutData.stats : defaultStats)"
                   :key="idx" class="data-item">
                <span class="data-num">{{ stat.number }}</span>
                <span class="data-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 当 aboutData 为空时的后备显示 -->
        <div v-else class="about-wrap">
          <div class="about-img">
            <img src="/images/steel-warehouse.jpg" alt="伟邺钢铁仓库" loading="lazy"
                 onerror="this.src='https://picsum.photos/id/104/600/400'">
          </div>
          <div class="about-content">
            <h3>佛山市伟邺钢铁有限公司</h3>
            <p>
              公司坐落于钢铁之都广东乐从钢铁世界，仓库面积5000平方，常年库存10000吨。主营镀锌方管、镀锌扁钢、镀锌钢管、镀锌角钢、无缝钢管、镀锌圆钢、镀锌槽钢、H型钢、工字钢、钢板等材料，致力于光伏发电、防雷接地、中央空调管道、消防管道等工程专用钢材材料配套。</p>
            <p>
              配套有热浸镀锌、激光切割、焊接、打砂喷漆等加工设备，年销售额五千万以上，职工人数50人。与鞍钢、柳钢、宝钢、衡阳华菱、天津友发等国内多家钢厂建立了长期战略合作关系，对产品质量和交货期都有严格保证，价格优惠，服务到位。</p>
            <div class="about-data">
              <div class="data-item"><span class="data-num">5000㎡</span><span class="data-label">仓储面积</span></div>
              <div class="data-item"><span class="data-num">10000吨</span><span class="data-label">常备库存</span></div>
              <div class="data-item"><span class="data-num">50人</span><span class="data-label">专业团队</span></div>
              <div class="data-item"><span class="data-num">5000万+</span><span class="data-label">年销售额</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 加工配套服务模块 -->
    <section class="section" id="service">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 服务介绍
        </div>
        <div class="section-title">
          <h2>加工配套能力</h2>
          <p>热浸镀锌 · 激光切割 · 焊接 · 打砂喷漆 · 非标件定制，实现“材料+加工”一体化服务</p>
        </div>
        <div class="service-wrap" v-if="serviceList.length">
          <div v-for="item in serviceList" :key="item.id" class="service-card">
            <img :src="getImageUrl(item.image)" :alt="item.title" class="service-img" loading="lazy">
            <div class="service-content">
              <h3>{{ item.title }}</h3>
              <div class="service-tags">
                <span v-for="tag in item.tags" :key="tag" class="service-tag">{{ tag }}</span>
              </div>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </div>
        <!-- 后备服务列表（仅用于演示，实际会被 API 数据覆盖） -->
        <div v-else class="service-wrap">
          <div class="service-card">
            <img src="/images/galvanizing.jpg" alt="热浸镀锌" class="service-img"
                 onerror="this.src='https://picsum.photos/id/30/400/220'">
            <div class="service-content">
              <h3>热浸镀锌</h3>
              <div class="service-tags"><span class="service-tag">防锈防腐</span><span
                  class="service-tag">国标厚度</span></div>
              <p>产品表面处理，增强耐候性，适用于户外工程及高标准防腐要求。</p>
            </div>
          </div>
          <div class="service-card">
            <img src="/images/laser-cut.jpg" alt="激光切割" class="service-img"
                 onerror="this.src='https://picsum.photos/id/42/400/220'">
            <div class="service-content">
              <h3>激光切割</h3>
              <div class="service-tags"><span class="service-tag">精度高</span><span class="service-tag">速度快</span>
              </div>
              <p>精密加工，异形件定制，满足复杂图纸需求。</p>
            </div>
          </div>
          <div class="service-card">
            <img src="/images/welding.jpg" alt="焊接加工" class="service-img"
                 onerror="this.src='https://picsum.photos/id/134/400/220'">
            <div class="service-content">
              <h3>焊接 & 打砂喷漆</h3>
              <div class="service-tags"><span class="service-tag">结构件焊接</span><span
                  class="service-tag">表面处理</span></div>
              <p>专业团队，支持非标件加工、来图定制，缩短客户生产周期。</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 合作案例模块（原客户案例，匹配文档“近期典型合作案例”） -->
    <section class="section" id="case">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 合作案例
        </div>
        <div class="section-title">
          <h2>近期典型合作案例</h2>
          <p>稳定的产品质量、高效的交付能力，赢得了众多客户的信赖与好评</p>
        </div>
        <div class="case-wrap">
          <div v-for="(item, idx) in filteredCaseList" :key="idx" class="case-card">
            <img :src="item.imgUrl" :alt="item.title" class="case-img" loading="lazy">
            <div class="case-content">
              <div class="case-tags">
                <span v-for="tag in getCaseTags(item.tag)" :key="tag" class="case-tag">{{ tag }}</span>
              </div>
              <h3>{{ item.title }}</h3>
              <p class="case-desc">{{ item.desc }}</p>
              <div class="case-detail" v-if="item.desc.length > 50">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 常见问题模块（可保留通用问题，或根据钢铁行业定制，但数据仍来自API） -->
    <section class="section" id="faq">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 常见问题
        </div>
        <div class="section-title">
          <h2>常见问题</h2>
          <p>解答关于钢材采购、加工、运输等环节的疑问，让合作更顺畅</p>
        </div>
        <div class="faq-wrap">
          <div v-for="(item, idx) in faqList" :key="idx" class="faq-item" :class="{ active: activeFaq.length === 0 || activeFaq.includes(idx) }">
            <div class="faq-question" @click="toggleFaq(idx)">{{ item.question }}</div>
            <div class="faq-answer">
              <p v-html="formatAnswer(item.answer)"></p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 荣誉资质模块（保持原有结构，但文案略调） -->
    <section class="section" id="honor">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 荣誉资质
        </div>
        <div class="section-title">
          <h2>荣誉资质</h2>
          <p>与国内多家知名钢厂建立长期战略合作，从源头把控质量</p>
        </div>
        <div class="honor-wrap">
          <div v-for="(item, idx) in honorList" :key="idx" class="honor-card">
            <div class="honor-img-wrap">
              <img :src="item.imgUrl" :alt="item.name" loading="lazy">
            </div>
            <div class="honor-card-content">
              <h3>{{ item.name }}</h3>
              <span class="honor-type">{{ getHonorType(item.name) }}</span>
              <p class="honor-card-desc" v-if="item.description">{{ item.description }}</p>
              <p class="honor-code" v-if="item.certNumber"><strong>证书编号：</strong>{{ item.certNumber }}</p>
              <p class="honor-check" v-if="item.verifyUrl || item.verifyMethod">
                <strong>核验渠道：</strong>{{ item.verifyMethod || '可通过相关主管部门官网查询' }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们模块（替换为伟邺钢铁联系方式） -->
    <section class="section" id="contact">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 联系我们
        </div>
        <div class="section-title">
          <h2>联系我们</h2>
          <p>欢迎莅临广东乐从钢铁世界实地考察，或致电咨询</p>
        </div>
        <div class="contact-wrap">
          <div class="contact-info">
            <h3>佛山市伟邺钢铁有限公司</h3>
            <div class="contact-item">
              <h4>公司地址</h4>
              <p>广东省佛山市顺德区乐从钢铁世界D1区钢铁世界大道05号</p>
            </div>
            <div class="contact-item">
              <h4>咨询热线</h4>
              <p>13168579111/18823109777/0757-28861573</p>
            </div>
            <div class="contact-item">
              <h4>营业时间</h4>
              <p>周一至周日 08:00-18:00</p>
            </div>
            <div class="contact-item">
              <h4>电子邮箱</h4>
              <p>464300817@qq.com</p>
            </div>
          </div>
          <div class="contact-form">
            <h3>在线询价</h3>
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="name">您的姓名</label>
                <input type="text" id="name" placeholder="请输入您的姓名" required v-model="formData.name">
              </div>
              <div class="form-group">
                <label for="phone">联系电话</label>
                <input type="tel" id="phone" placeholder="请输入您的手机号码" required v-model="formData.phone">
              </div>
              <div class="form-group">
                <label for="type">需求材料</label>
                <input type="text" id="type" placeholder="例如：镀锌方管、无缝钢管等" v-model="formData.courseType">
              </div>
              <div class="form-group">
                <label for="content">咨询内容</label>
                <textarea id="content" placeholder="请说明规格、数量或加工要求，我们将尽快回复"
                          v-model="formData.message"></textarea>
              </div>
              <button type="submit" class="submit-btn">提交询价</button>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer>
      © 2026 佛山市伟邺钢铁有限公司 版权所有 | <a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2026058191号</a> | <a href="https://www.beian.gov.cn/" target="_blank" rel="noopener noreferrer">
          <img src="../../public/AAA.png"
                 alt="公安备案图标"
                 width="16" height="17"
                 style="vertical-align: middle; margin-right: 4px;">
          粤公网安备44060602003292号</a> | 
      <span class="sigma-tag">sigma</span>
    </footer>
  </div>
</template>

<script>
import * as api from '@/api'

export default {
  name: 'App',
  data() {
    return {
      menuOpen: false,
      activeLink: 'home',
      activeFaq: [], // 空数组表示全部展开
      activeCaseFilter: 'all',
      loading: true,
      formData: {
        name: '',
        phone: '',
        courseType: '',
        message: ''
      },
      navItems: [
        {id: 'about', name: '企业介绍'},
        {id: 'service', name: '加工服务'},
        {id: 'case', name: '合作案例'},
        {id: 'faq', name: '常见问题'},
        {id: 'honor', name: '荣誉资质'},
        {id: 'contact', name: '联系我们'}
      ],
      aboutData: null,
      serviceList: [],
      caseList: [],
      faqList: [],
      honorList: [],
      // 后备数据（当 API 返回空时显示）
      defaultAboutParagraphs: [
        '公司坐落于钢铁之都广东乐从钢铁世界，仓库面积5000平方，常年库存10000吨。主营镀锌方管、镀锌扁钢、镀锌钢管、镀锌角钢、无缝钢管、镀锌圆钢、镀锌槽钢、H型钢、工字钢、钢板等材料，致力于光伏发电、防雷接地、中央空调管道、消防管道等工程专用钢材材料配套。',
        '配套有热浸镀锌、激光切割、焊接、打砂喷漆等加工设备，年销售额五千万以上，职工人数50人。与鞍钢、柳钢、宝钢、衡阳华菱、天津友发等国内多家钢厂建立了长期战略合作关系。'
      ],
      defaultStats: [
        {number: '5000㎡', label: '仓储面积'},
        {number: '10000吨', label: '常备库存'},
        {number: '50人', label: '专业团队'},
        {number: '5000万+', label: '年销售额'}
      ]
    }
  },
  computed: {
    filteredCaseList() {
      if (this.activeCaseFilter === 'all') {
        return this.caseList
      }
      const enterpriseTags = ['企业', '工程', '合作', '批量', '厂房']
      return this.caseList.filter(item => {
        if (this.activeCaseFilter === 'enterprise') {
          return enterpriseTags.some(tag => item.tag && item.tag.includes(tag))
        }
        return true
      })
    }
  },
  async mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    await this.fetchData();
    this.activeFaq = []
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    getImageUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      if (path.startsWith('/server/')) return path.replace('/server', '')
      return path
    },
    async fetchData() {
      this.loading = true;
      try {
        const [aboutData, serviceList, caseList, faqList, honorList] = await Promise.all([
          api.getAbout(),
          api.getServices(),
          api.getCaseList(),
          api.getFaqList(),
          api.getHonorList()
        ]);
        this.aboutData = aboutData;
        this.serviceList = Array.isArray(serviceList) ? serviceList : [];
        this.caseList = Array.isArray(caseList) ? caseList : (caseList.data || []);
        this.faqList = Array.isArray(faqList) ? faqList : (faqList.data || []);
        this.honorList = Array.isArray(honorList) ? honorList : (honorList.data || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        this.loading = false;
      }
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const top = element.offsetTop - offset;
        window.scrollTo({top, behavior: 'smooth'});
      }
      if (this.menuOpen) {
        this.menuOpen = false;
      }
    },
    handleScroll() {
      const scrollPos = window.scrollY + 100;
      let currentId = 'home';
      for (let item of this.navItems) {
        const section = document.getElementById(item.id);
        if (section) {
          const offsetTop = section.offsetTop;
          const offsetBottom = offsetTop + section.offsetHeight;
          if (scrollPos >= offsetTop && scrollPos < offsetBottom) {
            currentId = item.id;
            break;
          }
        }
      }
      this.activeLink = currentId;
    },
    toggleFaq(index) {
      if (this.activeFaq.length === 0) {
        // 当前全部展开，收起单个：初始化数组只包含这个索引
        this.activeFaq = this.faqList.map((_, i) => i).filter(i => i !== index)
      } else if (this.activeFaq.includes(index)) {
        // 当前有收起，找到并移除
        this.activeFaq = this.activeFaq.filter(i => i !== index)
      } else {
        // 展开单个
        this.activeFaq.push(index)
      }
    },
    getCaseTags(tag) {
      if (!tag) return []
      return tag.split(/[,，]/).filter(t => t.trim())
    },
    getHonorType(name) {
      if (name.includes('合作') || name.includes('战略')) return '战略合作'
      if (name.includes('质量') || name.includes('ISO')) return '质量认证'
      if (name.includes('信用') || name.includes('AAA')) return '信用评级'
      return '荣誉称号'
    },
    formatAnswer(answer) {
      if (!answer) return ''
      return answer.replace(/\n/g, '<br>')
    },
    async handleSubmit() {
      try {
        this.formData.createdAt = new Date().toISOString()
        await api.submitForm(this.formData);
        alert(`询价已提交，我们将尽快与您联系！\n姓名：${this.formData.name}\n电话：${this.formData.phone}`);
        this.formData = {name: '', phone: '', courseType: '', message: ''};
      } catch (err) {
        alert('提交失败，请稍后重试');
      }
    }
  }
}
</script>

<style scoped>
/* 全局样式 - 与style-flag.html一致 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
}

.app-container {
  padding-top: 0;
}

.container {
  width: 1200px;
  margin: 0 auto;
}

section {
  padding: 80px 0;
}

section:nth-child(even) {
  background-color: var(--light-color);
}

.section-title {
  text-align: center;
  margin-bottom: 60px;
}

.section-title h2 {
  font-size: 36px;
  color: var(--primary-color);
  margin-bottom: 15px;
  font-weight: 700;
}

.section-title p {
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
}

.breadcrumb {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

.breadcrumb a {
  color: var(--primary-color);
}

header {
  width: 100%;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 999;
}

.header-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 700;
  color: var(--primary-color);
}

.sigma-tag {
  background-color: var(--primary-color);
  color: var(--white);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

nav ul {
  display: flex;
  gap: 35px;
  list-style: none;
}

nav ul li a {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-color);
  transition: color 0.3s;
  padding: 5px 0;
  border-bottom: 2px solid transparent;
  text-decoration: none;
}

nav ul li a:hover {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}

nav ul li a.active {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}

.mobile-menu {
  display: none;
  font-size: 24px;
  cursor: pointer;
}

/* Banner样式 */
.banner {
  width: 100%;
  height: 600px;
  background: linear-gradient(rgba(15, 76, 129, 0.9), rgba(15, 76, 129, 0.85)), url(https://img0.baidu.com/it/u=3234567890,1234567890&fm=253&fmt=auto&app=138&f=JPEG?w=1920&h=1080) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--white);
}

.banner-content h1 {
  font-size: 42px;
  margin-bottom: 20px;
  font-weight: 700;
}

.banner-content p {
  font-size: 20px;
  margin-bottom: 35px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.banner-btn {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.banner-btn a {
  padding: 12px 35px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
}

.btn-primary {
  background-color: var(--secondary-color);
  color: var(--white);
}

.btn-primary:hover {
  background-color: #e66a00;
  transform: translateY(-2px);
}

.btn-outline {
  background-color: transparent;
  border: 2px solid var(--white);
  color: var(--white);
}

.btn-outline:hover {
  background-color: var(--white);
  color: var(--primary-color);
}

/* 统计行样式 */
.banner .stats-row {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 30px 0;
  flex-wrap: wrap;
}

.banner .stat-item {
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 20px;
  border-radius: 40px;
  font-size: 16px;
}

.banner .stat-item strong {
  font-size: 24px;
  margin-right: 6px;
}

/* 企业介绍 */
.about-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.about-img img {
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.about-content {
  text-align: left;
}

.about-content h3 {
  font-size: 28px;
  color: var(--primary-color);
  margin-bottom: 24px;
}

.about-content p {
  font-size: 16px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
}

.about-data {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 30px;
}

.data-item {
  text-align: center;
  padding: 15px;
  background-color: var(--light-color);
  border-radius: 8px;
}

.data-num {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 5px;
}

.data-label {
  font-size: 14px;
  color: #666;
}

/* 加工配套服务 */
.service-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.service-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s;
}

.service-card:hover {
  transform: translateY(-5px);
}

.service-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.service-content {
  padding: 20px;
  text-align: left;
}

.service-content h3 {
  font-size: 22px;
  color: var(--primary-color);
  margin-bottom: 12px;
}

.service-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.service-tag {
  background-color: rgba(0, 51, 102, 0.1);
  color: var(--primary-color);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
}

.service-content p {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
}

/* 合作案例 */
.case-filter {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
}

.case-filter button {
  padding: 8px 20px;
  border: 1px solid #d0d7de;
  background-color: var(--white);
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.case-filter button.active {
  background-color: var(--primary-color);
  color: var(--white);
  border-color: var(--primary-color);
}

.case-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.case-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.case-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.case-content {
  padding: 20px;
  text-align: left;
}

.case-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.case-tag {
  background-color: var(--secondary-color);
  color: var(--white);
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
}

.case-content h3 {
  font-size: 18px;
  color: var(--primary-color);
  margin-bottom: 8px;
}

.case-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.case-detail {
  margin-top: 10px;
  font-size: 14px;
  color: #888;
}

/* 常见问题 */
.faq-wrap {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background-color: var(--white);
  border-radius: 10px;
  margin-bottom: 15px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06);
}

.faq-question {
  padding: 20px 25px;
  font-size: 17px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  color: var(--text-color);
}

.faq-question::after {
  content: "+";
  font-size: 22px;
  color: var(--primary-color);
}

.faq-item.active .faq-question::after {
  content: "-";
}

.faq-answer {
  display: none;
  padding: 0 25px 20px;
  color: #666;
  font-size: 15px;
  line-height: 1.8;
}

.faq-item.active .faq-answer {
  display: block;
}

/* 荣誉资质 */
.honor-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.honor-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.honor-img-wrap {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.honor-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.honor-card-content {
  padding: 20px;
  text-align: left;
}

.honor-card-content h3 {
  font-size: 20px;
  color: var(--primary-color);
  margin-bottom: 8px;
}

.honor-type {
  display: inline-block;
  background-color: rgba(0, 51, 102, 0.1);
  color: var(--primary-color);
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 10px;
}

.honor-card-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-top: 8px;
}

.honor-code, .honor-check {
  font-size: 13px;
  color: #888;
  margin-top: 6px;
}

/* 联系我们 */
.contact-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
}

.contact-info h3 {
  font-size: 28px;
  color: var(--primary-color);
  margin-bottom: 30px;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 25px;
}

.contact-item h4 {
  font-size: 18px;
  margin-bottom: 8px;
  color: var(--primary-color);
}

.contact-item p {
  font-size: 16px;
  line-height: 1.6;
  color: #666;
}

.contact-form {
  background-color: var(--white);
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.contact-form h3 {
  font-size: 24px;
  color: var(--primary-color);
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
  text-align: left;
  border: #C9D1D9;
}

.form-group label {
  display: block;
  font-size: 15px;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group input, .form-group textarea {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 15px;
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-btn:hover {
  background-color: #0C3D6A;
}

/* 页脚 */
footer {
  background-color: #0A2A48;
  color: #C9D1D9;
  padding: 30px 0 40px;
}

footer a {
    text-decoration: none;
    color: #888;
}

footer a:hover {
    text-decoration: underline;
}

footer img {
    border: none;               /* 去掉图片边框 */
}

.footer-wrap {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 60px;
  margin-bottom: 60px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 24px;
}

.footer-desc {
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 24px;
  max-width: 500px;
}

.footer-title {
  font-size: 18px;
  color: var(--white);
  margin-bottom: 24px;
  font-weight: 600;
}

.footer-nav ul {
  list-style: none;
}

.footer-nav ul li {
  margin-bottom: 16px;
  line-height: 1.6;
}

.footer-nav ul li a {
  font-size: 14px;
  transition: color 0.3s;
  color: #C9D1D9;
  text-decoration: none;
}

.footer-nav ul li a:hover {
  color: var(--secondary-color);
}

.footer-copyright {
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 13px;
  line-height: 1.8;
}

.footer-copyright .sigma-tag {
  display: inline-block;
  margin-left: 10px;
}

@media (max-width: 1200px) {
  .container {
    width: 95%;
  }
}

@media (max-width: 992px) {
  .about-wrap, .contact-wrap, .footer-wrap {
    grid-template-columns: 1fr 1fr;
  }

  .about-data {
    grid-template-columns: repeat(2, 1fr);
  }

  .service-wrap, .case-wrap {
    grid-template-columns: repeat(2, 1fr);
  }

  .honor-wrap {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  nav ul {
    position: fixed;
    top: 80px;
    left: -100%;
    flex-direction: column;
    background: var(--white);
    width: 100%;
    height: calc(100vh - 80px);
    padding: 40px 20px;
    gap: 30px;
    transition: all 0.3s ease;
  }

  nav ul.active {
    left: 0;
  }

  .mobile-menu {
    display: block;
  }

  .banner {
    height: 500px;
  }

  .banner-content h1 {
    font-size: 28px;
  }

  .section-title h2 {
    font-size: 28px;
  }

  .about-wrap, .contact-wrap, .footer-wrap, .case-wrap {
    grid-template-columns: 1fr;
  }

  .about-data {
    grid-template-columns: repeat(2, 1fr);
  }

  .service-wrap, .case-wrap {
    grid-template-columns: 1fr;
  }

  .case-filter {
    flex-wrap: wrap;
  }
}

</style>